import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik } from 'formik';
import Image from 'next/future/image';
import { useRouter } from 'next/router';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/solid';

import { signup } from '@/fetchers/signup';
import { useAlert } from '@/context/AlertProvider';
import { getLangCountry } from '@/helpers/languages';

import MyHead from '@/components/MyHead';
import Input from '@/components/v2/Forms/Input';
import Checkbox from '@/components/v2/Forms/Checkbox';
import { Button } from '@/components/v2/UI/Button';
import LinkTo from '@/components/LinkTo';

const Signup = () => {
  const alert = useAlert();
  const router = useRouter();
  const { t } = useTranslation();
  const { lang, country } = getLangCountry(router.query);

  const [submitSuccess, setSubmitSuccess] = useState(false);

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      newsletter: false,
      privacy: false
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string().required(t('REQUIRED_FIELD')),
      last_name: Yup.string().required(t('REQUIRED_FIELD')),
      email: Yup.string()
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
          t('INVALID_EMAIL')
        )
        .required(t('REQUIRED_FIELD')),
      password: Yup.string()
        .min(6, t('PASSWORD_MIN_LENGTH'))
        .required(t('REQUIRED_FIELD')),
      passwordConfirm: Yup.string()
        .oneOf([Yup.ref('password')], t('PASSWORD_MISSMATCH'))
        .required(t('REQUIRED_FIELD')),
      privacy: Yup.boolean().oneOf([true]).required()
    }),
    async onSubmit(values) {
      const { first_name, last_name, email, password, newsletter, privacy } =
        values;

      const { error } = await signup({
        first_name,
        last_name,
        email,
        password,
        newsletter,
        privacy,
        lang,
        country
      });

      if (error) {
        return alert.error(t(error.response?.data?.message || 'GENERIC_ERROR'));
      }

      setSubmitSuccess(true);
    }
  });

  return (
    <Fragment>
      <MyHead />

      <div className="section space-y-3">
        <div className="flex items-center space-x-3">
          <Button variant="secondary-dark" onClick={router.back}>
            <ArrowLeftIcon className="h-3 w-3" />
          </Button>

          <p className="text-xs md:text-base uppercase tracking-wide">
            {t('GO_BACK')}
          </p>
        </div>

        <div className="grid gap-y-6 lg:grid-cols-2 lg:gap-y-0">
          <section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-black/50" />

            <Image
              src="/v2/signup/hero.jpg"
              alt="Checkout"
              width={0}
              height={0}
              sizes="100vw"
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 text-white space-y-6 p-6 md:p-10 xl:p-16">
              <h1 className="h2">{t('P_SIGNUP.TITLE')}</h1>
              <p className="text-body">{t('P_SIGNUP.DESC')}</p>
            </div>
          </section>

          <section className="space-y-16 lg:px-16">
            {submitSuccess ? (
              <div className="h-full flex justify-center items-center">
                <p>{t('SIGNUP_CONFIRM_TEXT')}</p>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={formik.handleSubmit}>
                <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-6">
                  <Input
                    type="text"
                    name="first_name"
                    label={t('NAME')}
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    error={formik.errors.first_name}
                    required
                  />

                  <Input
                    type="text"
                    name="last_name"
                    label={t('SURNAME')}
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    error={formik.errors.last_name}
                    required
                  />
                </div>

                <Input
                  type="email"
                  name="email"
                  label={t('MAIL_ADDRESS')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.errors.email}
                  required
                />

                <Input
                  type="password"
                  name="password"
                  label="Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.errors.password}
                  required
                />

                <Input
                  type="password"
                  name="passwordConfirm"
                  label={t('CONFIRM_PASSWORD')}
                  value={formik.values.passwordConfirm}
                  onChange={formik.handleChange}
                  error={formik.errors.passwordConfirm}
                  required
                />

                <Checkbox
                  name="newsletter"
                  checked={formik.values.newsletter}
                  onChange={formik.handleChange}
                >
                  {t('NEWSLETTER_SUBSCRIBE_CONSENT')}
                </Checkbox>

                <Checkbox
                  name="privacy"
                  checked={formik.values.privacy}
                  onChange={formik.handleChange}
                  error={formik.errors.privacy}
                  required
                >
                  <span>{t('DATA_CONSENT')} </span>
                  <span>
                    <LinkTo
                      href="/terms-and-conditions"
                      className="underline"
                      aria-label={t('DATA_CONSENT')}
                    >
                      {t('CLICK_HERE')}
                    </LinkTo>
                  </span>
                </Checkbox>

                <Button
                  type="submit"
                  variant="dark"
                  loading={formik.isSubmitting}
                  className="w-full"
                >
                  {t('CREATE_ACCOUNT')}
                </Button>
              </form>
            )}
          </section>
        </div>
      </div>
    </Fragment>
  );
};

export default Signup;
